import { FC, useState } from 'react'
import { InputDataGrid, InputDataGridRow } from '@rfh/ui/components/Inputs'
import FormStepsStore from '../../stores/forms/FormStepsStore'
import produce from 'immer'
import { toJS } from 'mobx'
import { TransactionInputFormData } from './types'
import { v4 } from 'uuid'
import { observer } from 'mobx-react'
import { useTransactionValidation } from '../../hooks/useTransactionValidation'
import FormStepsNavigation from './FormStepsNavigation'
import { Box } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import AppLoadingSplash from '../AppLoadingSplash'
import globalStore from '../../stores/GlobalStore'
import { useTranslation } from 'react-i18next'
import {
  defaultTransactionData,
  gridColumns,
} from '../../stores/forms/transactionFormStepsStore'
import { CollectingType, FUSTCODE555, ReturningType } from 'src/services/api/types'
import { getActiveFormStepsStore } from 'src/stores/forms/activeFormStepsStore'

const TransactionDataGridForm: FC<{
  formStepsStore: FormStepsStore<any, TransactionInputFormData>
}> = ({ formStepsStore }) => {
  const { columns, errors, rows, allFustCodesIsLoading } =
    useTransactionValidation(formStepsStore)

  const history = useHistory()
  const { t } = useTranslation()
  const [displayErrors, setDisplayErrors] = useState<boolean>(false)

  const resetTransactionIfNeeded = () => {
    const store = getActiveFormStepsStore()

    if (store && !store.isEditMode) {
      store.resetTransactionData()
    }
  }

  const showNotificationError = () => {
    globalStore.setGlobalAppError({
      errorTitle: t('general:errors.transactionErrorTitle'),
      errorSummary: t('general:errors.transactionErrorMessage'),
    })
  }

  const showFustcodeNotificationError = () => {
    globalStore.setGlobalAppError({
      errorTitle: t('general:errors.transactionErrorTitle'),
      errorSummary: t('general:errors.transactionDepotError'),
    })
  }

  if (allFustCodesIsLoading) return <AppLoadingSplash />

  return (
    <>
      <InputDataGrid
        rows={toJS(rows)}
        columns={gridColumns.map(item => {
          if (item.field === 'col2') {
            return {
              ...item,
              headerName: t('transactions:tr'),
            }
          } else if (item.field === 'col3') {
            return {
              ...item,
              headerName: t('transactions:lp'),
            }
          } else if (item.field === 'col4') {
            return {
              ...item,
              headerName: t('transactions:ps'),
            }
          }
          return item
        })}
        children={
          <InputDataGridRow
            errors={displayErrors ? errors : []}
            rows={toJS(rows)}
            columns={columns}
            handleAddRow={() => {
              setDisplayErrors(true)
              if (errors.length > 0) {
                const hasFustCodeError = errors.some(
                  err => err.colName === 'col1' && err.validation?.type === 'fustCode'
                )
                return hasFustCodeError
                  ? showFustcodeNotificationError()
                  : showNotificationError()
              }
              const newRow = {
                ...defaultTransactionData.rows[0],
                id: v4(),
              }
              formStepsStore.updateTransactionRows(oldRows => [
                ...oldRows.map(row => ({
                  ...row,
                  col2: {
                    value: String(row.col2.value).length === 0 ? 0 : row.col2.value,
                  },
                  col3: {
                    value: String(row.col3.value).length === 0 ? 0 : row.col3.value,
                  },
                  col4: {
                    value: String(row.col4.value).length === 0 ? 0 : row.col4.value,
                  },
                })),
                newRow,
              ])

              setDisplayErrors(false)
            }}
            handleChangeTextField={(event, field, column, row) => {
              const inputValue = event.target.value.toLowerCase()
              const isNumberWithoutLeadingZero = /^(?!00)([1-9][0-9]*)$/.test(
                inputValue
              )

              const maxLength = field === 'col1' ? 3 : field === 'col4' ? 4 : 2

              const newValue = isNumberWithoutLeadingZero
                ? event.target.value.substring(0, maxLength)
                : ''

              if (field === 'col1' && newValue === FUSTCODE555) {
                const transactionType: ReturningType | CollectingType | undefined =
                  formStepsStore.formData.information.transactieType

                const collectingTypes: CollectingType[] = [
                  'UitgifteDock',
                  'UitgifteMobiel',
                  'UitgifteVast',
                ]

                if (collectingTypes.includes(transactionType as CollectingType)) {
                  globalStore.setGlobalAppWarning({
                    warningTitle: t('general:warnings.transactionWarningTitle'),
                    warningSummary: t('general:warnings.fustTypeCannotBeOrderedYet'),
                    warningLink: t('general:warnings.fustTypeCannotBeOrderedYetLink'),
                  })
                }
              }

              const newRows = toJS(formStepsStore.formData.transaction.rows).map(item =>
                row.id === item.id
                  ? {
                      ...item,
                      [field]: {
                        ...item[field],
                        value: newValue,
                      },
                    }
                  : item
              )

              formStepsStore.setFormData(
                produce(toJS(formStepsStore.formData), draftState => {
                  draftState.transaction.rows = newRows
                })
              )
            }}
            handleDeleteRow={(_event, row) => {
              formStepsStore.removeTransactionRow(row.id)
            }}
          />
        }
      />
      <Box m={3}>
        <FormStepsNavigation
          showConfirm={false}
          enableNext={true}
          enableBack={formStepsStore.iterator.hasPrevious()}
          onClickBack={() => {
            resetTransactionIfNeeded()
            history.push(formStepsStore.skipToPreviousPath)
          }}
          onClickNext={() => {
            setDisplayErrors(true)
            if (errors.length <= 0) {
              history.push(formStepsStore.skipToNextPath)
            } else {
              const hasFustCodeError = errors.some(
                err => err.colName === 'col1' && err.validation?.type === 'fustCode'
              )
              hasFustCodeError
                ? showFustcodeNotificationError()
                : showNotificationError()
            }
          }}
        />
      </Box>
    </>
  )
}

export default observer(TransactionDataGridForm)
